/**
 * Client-side reverse geocoding via GeoJSON ray-casting.
 * No external API calls — loads country polygons once and tests point-in-polygon.
 * Also provides raw GeoJSON features for highlight rendering.
 */

import { pointInPolygon } from '@/utils/geo';

export interface CountryInfo {
  name: string;
  code: string;
}

/** Raw GeoJSON feature as loaded from the dataset. */
export interface RawCountryFeature {
  type: 'Feature';
  properties: { name: string; code: string };
  geometry: {
    type: 'Polygon' | 'MultiPolygon';
    coordinates: number[][][] | number[][][][];
  };
}

interface CountryFeature {
  name: string;
  code: string;
  polygons: [number, number][][];
}

let countryFeatures: CountryFeature[] = [];
/** Raw GeoJSON features indexed by country code for highlight rendering. */
const rawFeaturesByCode = new Map<string, RawCountryFeature>();
let initialized = false;

/** Cache of last detection result to avoid repeated ray-casting for nearby queries. */
let cachedResult: { lat: number; lng: number; result: CountryInfo | null } | null = null;
const CACHE_THRESHOLD_DEG = 0.1;

/**
 * Initialize country detection by loading the GeoJSON dataset.
 * Must be called before detectCountry().
 */
export async function initCountryDetection(): Promise<void> {
  if (initialized) return;

  try {
    const response = await fetch('/data/countries.geojson');
    if (!response.ok) {
      console.warn(`[Sentinel] Failed to load countries GeoJSON: ${response.status}`);
      return;
    }

    const geojson = await response.json();
    countryFeatures = parseCountryFeatures(geojson);

    // Store raw features for highlight rendering
    rawFeaturesByCode.clear();
    for (const feature of geojson.features ?? []) {
      const code = feature.properties?.code;
      const geometry = feature.geometry;
      if (code && geometry && (geometry.type === 'Polygon' || geometry.type === 'MultiPolygon')) {
        rawFeaturesByCode.set(code, feature as RawCountryFeature);
      }
    }

    initialized = true;
    console.log(`[Sentinel] Country detection loaded: ${countryFeatures.length} countries`);
  } catch (err) {
    console.warn('[Sentinel] Error loading countries GeoJSON:', err);
  }
}

/** Parse GeoJSON FeatureCollection into our internal format. */
function parseCountryFeatures(geojson: {
  features?: Array<{
    properties?: { name?: string; code?: string };
    geometry?: { type?: string; coordinates?: unknown };
  }>;
}): CountryFeature[] {
  const features: CountryFeature[] = [];

  for (const feature of geojson.features ?? []) {
    const name = feature.properties?.name;
    const code = feature.properties?.code;
    const geometry = feature.geometry;

    if (!name || !code || !geometry) continue;

    const polygons: [number, number][][] = [];

    if (geometry.type === 'Polygon') {
      const coords = geometry.coordinates as [number, number][][];
      if (coords?.[0]) {
        polygons.push(coords[0]);
      }
    } else if (geometry.type === 'MultiPolygon') {
      const multiCoords = geometry.coordinates as [number, number][][][];
      for (const poly of multiCoords) {
        if (poly?.[0]) {
          polygons.push(poly[0]);
        }
      }
    }

    if (polygons.length > 0) {
      features.push({ name, code, polygons });
    }
  }

  return features;
}

/**
 * Detect which country a coordinate falls within.
 * Returns null if not inside any known country polygon.
 *
 * Note: GeoJSON coordinates are [lng, lat], but the pointInPolygon function
 * expects (lat, lng) with polygon coords as [lng, lat] pairs — it tests
 * x (lng) intersection against y (lat) boundaries.
 */
export function detectCountry(lat: number, lng: number): CountryInfo | null {
  if (!initialized || countryFeatures.length === 0) return null;

  // Check cache — return cached result if query is within threshold
  if (
    cachedResult &&
    Math.abs(lat - cachedResult.lat) < CACHE_THRESHOLD_DEG &&
    Math.abs(lng - cachedResult.lng) < CACHE_THRESHOLD_DEG
  ) {
    return cachedResult.result;
  }

  let result: CountryInfo | null = null;

  for (const feature of countryFeatures) {
    for (const polygon of feature.polygons) {
      if (pointInPolygon(lat, lng, polygon)) {
        result = { name: feature.name, code: feature.code };
        break;
      }
    }
    if (result) break;
  }

  // Update cache
  cachedResult = { lat, lng, result };

  return result;
}

/**
 * Get the raw GeoJSON feature for a country code.
 * Used by the highlight layer to render country polygons.
 * Returns null if country not found or detection not initialized.
 */
export function getCountryGeoJson(countryCode: string): RawCountryFeature | null {
  return rawFeaturesByCode.get(countryCode) ?? null;
}

/**
 * Get all raw country features (empty until initCountryDetection resolves).
 * Used by choropleth layers that paint the whole country set.
 */
export function getAllCountryFeatures(): RawCountryFeature[] {
  return Array.from(rawFeaturesByCode.values());
}

/** Check if country detection has been initialized. */
export function isCountryDetectionReady(): boolean {
  return initialized;
}
